import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { LanguageKeys } from 'src/common/constants/languages';
import {
  normalizeTemplateForEditor,
  normalizeTemplateForStorage,
} from 'src/helpers/emailTemplateText';

export interface EmailTemplateLocaleFields {
  subject: string;
  greeting: string;
  intro: string;
  signature: string;
  actionInstructions: string;
  buttonText: string;
  rejectInstructions: string;
  rejectButtonText: string;
  outro: string;
  outroHtml: string;
}

export interface EmailTemplateListItem {
  key: string;
  adminLabel: string;
  helpText: string;
  updatedAt?: string;
  hasCustomContent?: boolean;
}

export interface EmailTemplateRecord {
  key: string;
  adminLabel: string;
  helpText: string;
  locales: Record<LanguageKeys, EmailTemplateLocaleFields>;
}

const emptyLocale = (): EmailTemplateLocaleFields => ({
  subject: '',
  greeting: '',
  intro: '',
  signature: '',
  actionInstructions: '',
  buttonText: '',
  rejectInstructions: '',
  rejectButtonText: '',
  outro: '',
  outroHtml: '',
});

interface EmailTemplatesState {
  templates: EmailTemplateListItem[];
  current: EmailTemplateRecord | null;
  isLoading: boolean;
  fetchTemplates: () => Promise<void>;
  fetchTemplate: (key: string) => Promise<EmailTemplateRecord | null>;
  saveTemplate: (record: EmailTemplateRecord) => Promise<void>;
  seedTemplates: () => Promise<void>;
  clearCurrent: () => void;
}

const useEmailTemplatesStore = create<EmailTemplatesState>((set, get) => ({
  templates: [],
  current: null,
  isLoading: false,

  fetchTemplates: async () => {
    set({ isLoading: true });
    try {
      const { data } = await SECURE_API.get<EmailTemplateListItem[]>('emailTemplates/');
      set({ templates: data, isLoading: false });
    } catch {
      set({ templates: [], isLoading: false });
    }
  },

  fetchTemplate: async (key: string) => {
    set({ isLoading: true });
    try {
      const encoded = encodeURIComponent(key);
      const { data } = await SECURE_API.get<EmailTemplateRecord>(`emailTemplates/${encoded}`);
      const normalized = normalizeTemplateForEditor(data);
      set({ current: normalized, isLoading: false });
      return normalized;
    } catch {
      set({ current: null, isLoading: false });
      return null;
    }
  },

  saveTemplate: async (record: EmailTemplateRecord) => {
    set({ isLoading: true });
    const encoded = encodeURIComponent(record.key);
    const payload = normalizeTemplateForStorage(record);
    await SECURE_API.put(`emailTemplates/${encoded}`, {
      adminLabel: payload.adminLabel,
      helpText: payload.helpText,
      locales: payload.locales,
    });
    set({ current: payload, isLoading: false });
    await get().fetchTemplates();
  },

  seedTemplates: async () => {
    set({ isLoading: true });
    await SECURE_API.post('emailTemplates/seed');
    set({ isLoading: false });
    await get().fetchTemplates();
  },

  clearCurrent: () => set({ current: null }),
}));

export { emptyLocale };
export default useEmailTemplatesStore;
